import {
  ChevronDown,
  ChevronUp,
  Copy,
  Fullscreen as IVAIcon,
  Image as MediaIcon,
} from 'lucide-react';
import React, { useState } from 'react';
import { buttonKeys, headerBtnStyles } from './constants';
import { ChatSummaryTable, IVATable } from './ChatSummaryTables';
import {
  ComplaintMedia,
  CustomerComment,
  AgentComments,
  SessionComplaintType,
  SessionDisposition,
} from './ChatSummaryElements';
import { MetricsForJumboEvent } from '../../../analytics/constants';
import { IVAResolutionsBtnClick } from '../../../analytics/context-passing/events';
import InfoToggle from '../../ComponentsV1/InfoToggle';

const ChatSessionComp = ({ session, userId, orderId, cartId }) => {
  // current open session detail
  const [currentSelectedComponent, setCurrentSelectedComponent] =
    useState(null);

  if (!session) return null;

  const {
    session_id = '',
    install_ts,
    disposition = [],
    media = [],
    iva_resolution = {},
    comments = '',
    agent_comments = [],
    complaints = [],
  } = session;

  let sessionDate = '';

  if (install_ts) {
    const timestamp =
      install_ts.toString().length === 10 ? install_ts * 1000 : install_ts;
    const date = new Date(timestamp);
    if (!isNaN(date)) {
      sessionDate = date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }

  const handleIVAResolutionClick = () => {
    // IVA resolution button click event
    const properties = {
      [MetricsForJumboEvent.customer_id]: userId,
      [MetricsForJumboEvent.order_id]: orderId,
      [MetricsForJumboEvent.cart_id]: cartId,
      [MetricsForJumboEvent.feature_meta]: {
        session_id,
        complaint_type: complaints?.map((c) => c?.complaint_type)?.join(','),
      },
    };

    IVAResolutionsBtnClick(properties);

    setCurrentSelectedComponent((prev) =>
      prev === buttonKeys.ivaResolution ? null : buttonKeys.ivaResolution,
    );
  };

  return (
    <div className='tw-flex tw-flex-col tw-rounded-2xl tw-w-full tw-border tw-border-1 tw-border-solid tw-border-grey-200 tw-drop-shadow-sm'>
      {/* Session header */}
      <div className='tw-flex tw-items-center tw-justify-between tw-p-2 tw-w-full tw-bg-base-green tw-rounded-t-xl tw-h-[42px]'>
        {/* Session ID, date & time */}
        <div className='tw-flex tw-items-center tw-gap-5 tw-text-white tw-ml-2'>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <span
            className='tw-text-lg tw-font-medium tw-text-[1.4rem] tw-text-white tw-flex tw-items-center tw-gap-2'
            title='Click to copy session ID'
            onClick={() => {
              navigator.clipboard.writeText(session_id ?? 'No Session ID');
            }}
          >
            Session ID:
            <span className='tw-flex tw-items-center tw-cursor-pointer hover:tw-opacity-80 tw-transition-all tw-duration-300 tw-ease-in-out active:tw-scale-90'>
              {session_id} <Copy size={14} className='tw-ml-2' />{' '}
            </span>
          </span>
          <div className='tw-h-2 tw-w-2 tw-rounded-full tw-bg-white' />
          <span className='tw-text-[1.4rem] tw-font-medium tw-text-grey-100 tw-uppercase'>
            {sessionDate}
          </span>
        </div>

        {/* header buttons */}
        <div className='tw-flex tw-items-center tw-gap-2'>
          {iva_resolution && Object.keys(iva_resolution).length > 0 && (
            <button
              className={headerBtnStyles(
                currentSelectedComponent,
                buttonKeys.ivaResolution,
              )}
              onClick={handleIVAResolutionClick}
            >
              <IVAIcon size={18} /> IVA Resolution
            </button>
          )}
          {Array.isArray(media) && media.length > 0 && (
            <button
              className={headerBtnStyles(
                currentSelectedComponent,
                buttonKeys.complaintMedia,
              )}
              onClick={() => {
                setCurrentSelectedComponent((prev) =>
                  prev === buttonKeys.complaintMedia
                    ? null
                    : buttonKeys.complaintMedia,
                );
              }}
            >
              <MediaIcon size={18} /> Complaint Media
            </button>
          )}
        </div>
      </div>

      {/* Session details */}
      <div className='tw-p-3 tw-w-full tw-flex tw-flex-col tw-gap-3'>
        {/* Agent Information */}
        <div
          className={`tw-flex tw-items-start tw-w-full tw-rounded-2xl tw-bg-grey-100/30 tw-border tw-border-1 tw-border-solid tw-border-grey-200 tw-p-4 tw-gap-4`}
        >
          <SessionDisposition disposition={disposition} />
          <AgentComments comments={agent_comments} />
        </div>

        {/* Complaint Tables */}
        {complaints?.map((complaintData, index) => {
          const {
            complaint_type = '',
            items_resolutions = [],
            node = '',
          } = complaintData || {};

          return (
            <InfoToggle
              openIcon={
                <ChevronDown
                  size={20}
                  className='tw-mt-[1px] tw-text-base-green tw-mr-4'
                />
              }
              closeIcon={
                <ChevronUp
                  size={20}
                  className='tw-mt-[1px] tw-text-base-green tw-mr-4'
                />
              }
              isOpen={true}
              key={`${complaint_type}-${index}`}
              title={
                <SessionComplaintType
                  complainType={complaint_type}
                  node={node}
                />
              }
              showBorder={true}
              borderStyles='tw-border !tw-rounded-2xl tw-border-solid tw-border-grey-200'
              styles='tw-h-full tw-bg-grey-100/30 tw-h-full hover:tw-bg-grey-100 tw-transition-all tw-duration-200 tw-ease-in-out'
            >
              <ChatSummaryTable items_resolutions={items_resolutions} />
            </InfoToggle>
          );
        })}

        {/* Comments */}
        <div className='tw-flex tw-items-start tw-w-full tw-rounded-2xl tw-gap-4 tw-bg-grey-100/30 tw-border tw-border-1 tw-border-solid tw-border-grey-200 tw-p-4'>
          <CustomerComment comments={comments} />
        </div>
      </div>

      {/* IVA resolution and Complaint Media */}
      <div
        className={`${
          currentSelectedComponent ? 'tw-h-auto' : 'tw-h-0'
        } tw-transition-all tw-duration-500 tw-ease-in-out`}
      >
        {/* IVA Resolution Details */}
        {currentSelectedComponent === buttonKeys.ivaResolution &&
          iva_resolution &&
          Object.keys(iva_resolution).length > 0 &&
          Object.keys(iva_resolution)
            // .reverse()
            .map((iva_session_index, index) => {
              const iva_resolution_object = iva_resolution[iva_session_index];

              if (
                !iva_resolution_object ||
                !Array.isArray(iva_resolution_object) ||
                iva_resolution_object.length === 0
              ) {
                return null;
              }

              return (
                <IVATable
                  data={iva_resolution_object}
                  key={`iva-session-${iva_session_index}`}
                  iva_session_index={Object.keys(iva_resolution).length - index}
                  isLatestSession={index === 0}
                />
              );
            })}

        {/* Complaint Media */}
        {currentSelectedComponent === buttonKeys.complaintMedia &&
          Array.isArray(media) &&
          media.length > 0 && <ComplaintMedia media={media} />}
      </div>
    </div>
  );
};

export default ChatSessionComp;
